/**
 * @file dataQueryForTSHostedCharts.util.ts
 * @description Builds the data-query payload (DataQueryInput) for a TS-hosted
 * chart on the host side, BEFORE the chart iframe has loaded, so data fetching
 * can start ~900ms earlier (the iframe's request then hits the warm Apollo cache
 * instead of going to the network again).
 *
 * # The byte-identity contract
 *
 * For that cache hit to happen the payload must be byte-identical to what the
 * iframe eventually sends — Apollo's cache key is the serialized variables.
 * Query generation is therefore delegated to the shared
 * `@thoughtspot/valkyrie-chart-query-builder` package (the same package the
 * iframe uses inside valkyrie-charts), bridged here via:
 *
 *   GraphQL ChartViz / AnswerColumn / CustomChartConfig
 *     ─(mapAnswerColumnToCustomColumns, mapCustomChartConfigToSDKConfig)→
 *   SDK ChartModel / ChartConfig
 *     ─(buildQueriesFromChartConfig + MN/MV strip, both from the package)→
 *   SDK Query[]
 *     ─(mapSDKQueryToDataQueryInput)→
 *   GraphQL DataQueryInput
 *
 * Three per-chart-type decisions — family, axis-encoding slot keys, and the
 * MN/MV strip rule — live in the package's chart-type registry
 * (valkyrie-chart-query-builder/src/chart-type.ts) and are consumed by BOTH
 * sides, so those cannot silently diverge. The post-build MN/MV strip below
 * uses the registry's predicate + the package's transform, mirroring
 * valkyrie BaseChart's getQueriesFromChartConfig SDK handler.
 *
 * Two payload-affecting decisions are NOT yet in the registry and are
 * resolved independently on each side — keep them in sync by hand when a new
 * chart type behaves unlike plain cartesian:
 *   - Totals-enabled flags: here via the package's getTotalsEnabledFlags
 *     (over a ChartViz-derived TotalsStateInputs bag); the iframe via its own
 *     valkyrie-charts/src/config/index.ts getTotalsEnabledFlags (over its
 *     ChartModel with visualOverrides merged in). The package mirrors the
 *     iframe: saved totals-summary (settings-v2 shape — cartesian flat,
 *     pivot nested) overlaid per-key by TSE summariesState overrides, plus
 *     the pivot structural clamp. Remaining known gap: chart-settings-v1
 *     deployments (the package assumes v2, the only enabled mode).
 *   - Query batch size: here via getDefaultBatchSize / max-data-points for
 *     cartesian; pivot uses the package's DATA_SIZE_LIMIT on both sides
 *     (the iframe hardcodes it and ignores max-data-points).
 *
 * # Adding a new TS-hosted chart type
 *
 *   1. Register it in the package's chart-type registry
 *      (valkyrie-chart-query-builder/src/chart-type.ts) — the exhaustive
 *      Record turns a missing entry into a compile error.
 *   2. Add its GraphQL → package mapping row(s) to GRAPHQL_TO_QB_CHART_TYPE
 *      below (both the Advanced* and any legacy enum name).
 *   3. Nothing else in this file should need to change.
 *
 * # Debugging duplicate GetAnswerDataByQuery calls
 *
 * A duplicate call means the prefetch payload didn't byte-match the iframe's.
 * Enable debug logging for 'ts-hosted-chart-prefetch' and diff the logged
 * queries against the iframe's ('QUERIES' log line in valkyrie BaseChart).
 * Historical causes, all of the same shape — a per-chart-type decision made
 * differently on the two sides: totals offset stamping (pivot), axis-encoding
 * slot keys (donut), MN/MV stripping (bubble/scatter).
 */

import {
    getChartType,
    getColumns,
} from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import {
    getDefaultBatchSize,
    isCustomChart,
    isTsHostedChart,
    newExperienceChartTypes,
} from '@thoughtspot/chart-lib/lib/core/chart-type-specification-service';
import { generateDefaultCustomChartConfigForTSHostedCharts } from '@thoughtspot/chart-lib/lib/core/custom-chart-config-utils';
import { envFlags } from '@thoughtspot/env-flags';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { create } from '@thoughtspot/logger';
import * as SessionService from '@thoughtspot/session-service';
import {
    mapAnswerColumnToCustomColumns,
    mapCustomChartConfigToSDKConfig,
    mapSDKQueryToDataQueryInput,
} from '@thoughtspot/ts-chart-app';
import { ChartModel } from '@thoughtspot/ts-chart-sdk';
import {
    buildQueriesFromChartConfig,
    ChartType as QbChartType,
    DATA_SIZE_LIMIT,
    getTotalsEnabledFlags,
    needsMnMvPostProcessingForType,
    stripMnMvFromQueries,
    TotalsStateInputs,
} from '@thoughtspot/valkyrie-chart-query-builder';
import {
    AnswerColumn,
    ChartType,
    ChartViz,
    CustomChartAxisConfig,
    CustomChartConfig,
    DataQueryInput,
    SimpleAxis,
} from '/@services/generated/graphql-types';

const logger = create('ts-hosted-chart-prefetch');

// ─── Feature flags ────────────────────────────────────────────────────────────

function isFlagOn(flag: FEATURE_FLAG_NAMES): boolean {
    return isFeatureEnabled(flag, { urlParamValue: envFlags.getValue(flag) });
}

/**
 * When on, MEASURE_NAMES/MEASURE_VALUES are computed client-side, so the BE
 * queries must NOT carry MN/MV columns.
 */
function isClientSideMnMvCalcEnabled(): boolean {
    // Mirror the iframe's resolution exactly: URL override → configInfo
    // fallback. Reading envFlags directly would only see the URL override /
    // env-flag default (undefined) and miss the tscli/config-backed backend
    // default, desyncing the prefetch host from the iframe (byte-identity
    // contract) and forcing an Apollo cache miss on every chart load.
    return SessionService.enableClientSideMnMvCalculation();
}

/**
 * When on, a pivot with no explicit totals saved still renders all four totals
 * (the iframe enables them by default — see valkyrie getTotalsEnabledFlags).
 */
function isPivotTotalsOnByDefault(): boolean {
    return isFlagOn(
        FEATURE_FLAG_NAMES.ENABLE_ADVANCED_PIVOT_TABLE_TOTALS_BY_DEFAULT,
    );
}

// ─── Column-availability filtering ────────────────────────────────────────────
// Drops any column id from the customChartConfig that no longer exists in the
// chart's columns (e.g. user removed it from the answer). Without this the
// downstream SDK adapters would silently skip the missing column and produce a
// chart-config that doesn't match what the iframe will generate from the same
// stale state.

/**
 * Filters a simple axis (FlatAxis or MergedAxis) to only include valid columns
 */
function filterSimpleAxis(
    axis: SimpleAxis,
    availableColumnIds: Set<string>,
): SimpleAxis | null {
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'FlatAxis') {
        return availableColumnIds.has(axis.column) ? axis : null;
    }
    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'MergedAxis') {
        const filteredColumns = axis.columns.filter((colId: string) =>
            availableColumnIds.has(colId),
        );
        if (filteredColumns.length === 0) return null;
        return { ...axis, columns: filteredColumns };
    }
    return null;
}

function filterAxis(
    axis: CustomChartAxisConfig,
    availableColumnIds: Set<string>,
): CustomChartAxisConfig | null {
    if (!axis) return null;

    // eslint-disable-next-line no-underscore-dangle
    if (axis.__typename === 'DualAxis') {
        const filteredPrimary = filterSimpleAxis(
            axis.primary,
            availableColumnIds,
        );
        const filteredSecondary = filterSimpleAxis(
            axis.secondary,
            availableColumnIds,
        );
        if (!filteredPrimary && !filteredSecondary) return null;
        return {
            ...axis,
            primary: filteredPrimary || axis.primary,
            secondary: filteredSecondary || axis.secondary,
        };
    }

    return filterSimpleAxis(axis, availableColumnIds);
}

function filterCustomChartConfigColumns(
    customChartConfigs: CustomChartConfig[],
    availableColumns: AnswerColumn[],
): CustomChartConfig[] {
    if (!customChartConfigs || customChartConfigs.length === 0) {
        return customChartConfigs;
    }

    const availableColumnIds = new Set(availableColumns.map(col => col.id));

    return customChartConfigs.map(config => {
        const filteredConfig = { ...config };
        if (!filteredConfig.dimensions) return filteredConfig;

        filteredConfig.dimensions = filteredConfig.dimensions
            .map(dimension => {
                // eslint-disable-next-line no-underscore-dangle
                if (dimension.__typename === 'AxisDrivenChartConfigDimension') {
                    const filteredAxes = (dimension.axes || [])
                        .map(axis => filterAxis(axis, availableColumnIds))
                        .filter(axis => axis !== null);

                    if (filteredAxes.length === 0) return null;
                    return { ...dimension, axes: filteredAxes };
                }

                if (dimension.columns) {
                    const filteredColumns = dimension.columns.filter(colId =>
                        availableColumnIds.has(colId),
                    );
                    if (filteredColumns.length === 0) return null;
                    return { ...dimension, columns: filteredColumns };
                }

                return dimension;
            })
            .filter(dimension => dimension !== null);

        return filteredConfig;
    });
}

// ─── GraphQL → package ChartType mapping ──────────────────────────────────────
// The package's ChartType enum is the closed set of types it knows how to
// generate queries for (string values match valkyrie's own ChartType enum).
// One row per GraphQL name; legacy (pre-Advanced) names map to the same
// package type as their Advanced counterpart.

const GRAPHQL_TO_QB_CHART_TYPE: Partial<Record<
    ChartType,
    QbChartType | null
>> = {
    [ChartType.AdvancedPivotTable]: QbChartType.Pivot,
    [ChartType.PivotTable]: QbChartType.Pivot,
    [ChartType.AdvancedDonut]: QbChartType.Donut,
    [ChartType.AdvancedColumn]: QbChartType.Column,
    [ChartType.Column]: QbChartType.Column,
    [ChartType.AdvancedStackedColumn]: QbChartType.StackedColumn,
    [ChartType.StackedColumn]: QbChartType.StackedColumn,
    [ChartType.AdvancedBar]: QbChartType.Bar,
    [ChartType.Bar]: QbChartType.Bar,
    [ChartType.AdvancedStackedBar]: QbChartType.StackedBar,
    [ChartType.StackedBar]: QbChartType.StackedBar,
    [ChartType.AdvancedLine]: QbChartType.Line,
    [ChartType.Line]: QbChartType.Line,
    [ChartType.AdvancedArea]: QbChartType.Area,
    [ChartType.Area]: QbChartType.Area,
    [ChartType.AdvancedStackedArea]: QbChartType.StackedArea,
    [ChartType.StackedArea]: QbChartType.StackedArea,
    [ChartType.AdvancedLineColumn]: QbChartType.LineColumn,
    [ChartType.LineColumn]: QbChartType.LineColumn,
    [ChartType.AdvancedLineStackedColumn]: QbChartType.LineStackedColumn,
    [ChartType.LineStackedColumn]: QbChartType.LineStackedColumn,
    [ChartType.AdvancedScatter]: QbChartType.Scatter,
    [ChartType.Scatter]: QbChartType.Scatter,
    [ChartType.AdvancedBubble]: QbChartType.Bubble,
    [ChartType.Bubble]: QbChartType.Bubble,
    // MuzeStudio queries are BE-generated — never prefetch.
    [ChartType.MuzeStudio]: null,
};

function mapGraphQLChartTypeToQb(chartType: ChartType): QbChartType | null {
    const mapped = GRAPHQL_TO_QB_CHART_TYPE[chartType];
    if (mapped !== undefined) {
        return mapped;
    }
    // Legacy `Unknown` family fallback — anything not in the table that
    // still reaches here (shouldPrefetchData already filtered MuzeStudio +
    // non-TS-hosted types) gets cartesian-style MN/MV handling, i.e. Column.
    // A new TS-hosted type missing its mapping row lands here silently and
    // would build Column-shaped queries (wrong encodings/strip → duplicate
    // network call), so leave a breadcrumb — this is the one checklist step
    // the compiler can't enforce.
    logger.warn(
        `No GRAPHQL_TO_QB_CHART_TYPE row for '${chartType}'; ` +
            'falling back to Column. Add a mapping row if this is a new TS-hosted chart type.',
    );
    return QbChartType.Column;
}

// ─── Totals state extraction ──────────────────────────────────────────────────
// Normalize the totals state out of a ChartViz into the package's input bag.
// See `getTotalsEnabledFlags` in the package for the resolution order; this
// function only does the field extraction.
function buildTotalsStateInputs(chartViz: ChartViz): TotalsStateInputs {
    const cv = chartViz.customVisualProps;
    // totals-summary is read ONLY from under `displayVisualProps` — the one
    // location the iframe reads under chart-settings-v2 (valkyrie
    // config/index.ts prefixes every totals path with displayVisualProps).
    // A legacy top-level customVisualProps['totals-summary'] is deliberately
    // ignored: the iframe ignores it too, and honoring it here made the
    // prefetch emit totals queries the iframe never sends.
    const totalsSummary =
        (cv as any)?.displayVisualProps?.['totals-summary'] ?? null;
    return {
        totalsSummary,
        summariesState:
            (chartViz.visualOverrides?.overrides?.displayProperties as any)
                ?.summariesState ?? null,
        pivotTotalsOnByDefault: isPivotTotalsOnByDefault(),
    };
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build initial data query for TS-hosted charts.
 *
 * @param chartViz - Chart visualization metadata from GetChartWithData
 */
export function buildInitialDataQueryForTSHostedCharts(
    chartViz: ChartViz,
): DataQueryInput | null {
    if (!chartViz) return null;

    const chartType = getChartType(chartViz);

    // Custom (BYOC) charts own their query generation — do not prefetch.
    if (isCustomChart(chartType)) return null;
    // Only TS-hosted charts.
    if (!isTsHostedChart(chartType)) return null;

    const qbChartType = mapGraphQLChartTypeToQb(chartType);
    if (qbChartType === null) return null;

    const columns = getColumns(chartViz);
    if (columns.length === 0) return null;

    const maxDataPoints =
        chartViz.customVisualProps?.displayVisualProps?.display?.[
            'max-data-points'
        ];
    // Pivot: the iframe's PivotChart passes valkyrie's DATA_SIZE_LIMIT
    // (=100001, same constant the package exports) unconditionally and never
    // consults max-data-points (pivot settings don't expose it; the prop can
    // only appear via retention across a chart-type switch). Honoring it here
    // stamped a different size than the iframe and broke cache dedup. Note
    // getDefaultBatchSize is also wrong for pivot when the viz reports the
    // legacy PivotTable name (100000 vs the iframe's 100001).
    // Non-pivot: an empty-string max-data-points falls back to the default,
    // as does an absent one (via ??).
    let defaultBatchSize: number;
    if (qbChartType === QbChartType.Pivot) {
        defaultBatchSize = DATA_SIZE_LIMIT;
    } else if (maxDataPoints === '') {
        defaultBatchSize = getDefaultBatchSize(chartType);
    } else {
        defaultBatchSize = maxDataPoints ?? getDefaultBatchSize(chartType);
    }

    // Pass size-only (no explicit offset:0) to match valkyrie-charts. The
    // package defaults the root query's offset to 0 via `params?.offset || 0`,
    // but its totals builder (`generateQueryParams`) copies offset verbatim —
    // so passing offset:0 here would stamp offset:0 on totals queries while
    // valkyrie's totals carry no offset key at all. The mismatch broke
    // Apollo cache dedup between the prefetch and the iframe's call.
    const queryParams = { size: defaultBatchSize };

    // For Muze phase-1 cartesian charts the BE owns default config generation.
    // Use the existing config if present; skip FE generation so we don't
    // override the BE-computed config with a stale FE-generated one.
    // (shouldPrefetchData already filters MuzeStudio out, but be defensive.)
    const isMuzeChart = newExperienceChartTypes.includes(chartType);
    let customChartConfig =
        chartViz.config.customChartConfig ||
        (isMuzeChart
            ? null
            : generateDefaultCustomChartConfigForTSHostedCharts(
                  chartViz,
                  chartType,
              ));

    if (customChartConfig) {
        customChartConfig = filterCustomChartConfigColumns(
            customChartConfig,
            columns,
        );
    }

    if (!customChartConfig?.length) return null;

    // GraphQL → SDK type adaptation. Convert each AnswerColumn ONCE and
    // share the resulting ChartColumns between the chartModel stub and the
    // config adapter (via its prebuilt-map parameter) — the adapter would
    // otherwise re-run mapAnswerColumnToCustomColumns per column internally.
    //
    // The structural cast below is needed because blink-v2's
    // `AnswerColumn` and ts-chart-app's `AnswerColumn` are generated
    // from the same schema in two separate codegen runs — structurally
    // identical, nominally distinct. Using `Parameters<...>` instead of
    // `as any` ties the cast to the function's actual signature so any
    // future change to the adapter's input shape surfaces here.
    const vizColumns = columns.map(col => ({ column: col }));
    const chartColumns = columns.map(col =>
        mapAnswerColumnToCustomColumns(col),
    );
    const chartColumnMap = Object.fromEntries(
        chartColumns.map(col => [col.id, col]),
    );
    const chartConfig = mapCustomChartConfigToSDKConfig(
        customChartConfig,
        (vizColumns as unknown) as Parameters<
            typeof mapCustomChartConfigToSDKConfig
        >[1],
        [],
        chartColumnMap,
    );
    // The package only reads chartModel.columns — all other ChartModel fields
    // are unused, so a minimal stub is sufficient.
    const chartModel = { columns: chartColumns } as ChartModel;

    // Resolve enabled totals from the saved visualProps (TSE overrides
    // applied per-key on top, mirroring the iframe's syncCustomVisualProps).
    // Passing chartConfig lets the resolver apply the pivot structural clamp
    // (the iframe's disabled-reset). Non-pivot config-derived support
    // (attribute count + trellis) is reconciled inside
    // buildQueriesFromChartConfig — the same reconciliation the iframe gets —
    // so the prefetch and the iframe can't emit different totals queries and
    // force a duplicate GetAnswerDataByQuery. (A persisted flag that no longer
    // applies to the current config, e.g. a column-total carried over after
    // switching a line-stacked-column to a bar whose y-axis has no attribute,
    // is dropped there.)
    const totalsFlags = getTotalsEnabledFlags(
        buildTotalsStateInputs(chartViz),
        qbChartType,
        chartConfig,
    );

    // Build queries WITH MN/MV included, then strip when required — the
    // identical two-step the iframe runs in valkyrie BaseChart's
    // getQueriesFromChartConfig SDK handler. Axis encodings are resolved
    // inside the package from its chart-type registry (same source the
    // valkyrie chart classes read), so they cannot diverge.
    const { queries } = buildQueriesFromChartConfig(
        qbChartType,
        chartModel,
        chartConfig,
        queryParams,
        undefined, // axisEncodings — from the package's chart-type registry
        totalsFlags,
    );

    const shouldStripMnMv =
        isClientSideMnMvCalcEnabled() ||
        needsMnMvPostProcessingForType(qbChartType, chartConfig);
    const finalQueries = shouldStripMnMv
        ? stripMnMvFromQueries(queries)
        : queries;

    const dataQueryInput = mapSDKQueryToDataQueryInput(finalQueries);

    // Counterpart of the iframe's 'QUERIES' log line (valkyrie BaseChart) —
    // diff the two when chasing duplicate GetAnswerDataByQuery calls.
    logger.debug('PREFETCH QUERIES', {
        chartType,
        qbChartType,
        strippedMnMv: shouldStripMnMv,
        queryCount: finalQueries.length,
        dataQueryInput,
    });

    return dataQueryInput;
}

/**
 * Check if early data fetch is enabled and beneficial for this chart
 */
export function shouldPrefetchData(chartViz: ChartViz | null): boolean {
    if (!chartViz) {
        return false;
    }

    const chartType = getChartType(chartViz);

    // Custom (BYOC) charts run arbitrary third-party query logic we can't
    // predict, so a prefetch could never be a reliable cache-key match — skip.
    if (isCustomChart(chartType)) {
        return false;
    }

    // Only prefetch for TS-hosted charts
    if (!isTsHostedChart(chartType) || chartType === ChartType.MuzeStudio) {
        return false;
    }

    // Must have columns - this is all we need now since we use ALL columns
    const columns = chartViz.columns || [];

    if (columns.length === 0) {
        return false;
    }

    return true;
}
